import { matchScore } from '@/lib/search-match'

export type SearchTemplate = {
  id: string
  title: string
  category: string
  description: string
  person: string | null
  work: string | null
  author: string | null
  icon: string | null
  habit: boolean
}

type TemplateLike = {
  id: string
  title: string
  category: string
  description: string
  collection: string | null
  inspiredBy: { work: string; author: string } | null
  blueprint: { person: string | null; icon?: string | null } | null
}

// Slim projection: what search needs and what's cheap to keep in localStorage.
export function toSearchTemplate(tpl: TemplateLike): SearchTemplate {
  return {
    id: tpl.id,
    title: tpl.title,
    category: tpl.category,
    description: tpl.description,
    person: tpl.blueprint?.person ?? null,
    work: tpl.inspiredBy?.work ?? null,
    author: tpl.inspiredBy?.author ?? null,
    icon: tpl.blueprint?.icon ?? null,
    habit: tpl.collection === 'blueprints' || !!tpl.blueprint,
  }
}

// Title, then "Inspired by" person / work / author and the blurb via matchScore's note tier.
export function templateScore(tpl: SearchTemplate, query: string): number {
  const base = matchScore({ title: tpl.title }, query)
  if (base > 0) return base
  if (query.trim().startsWith('#')) return 0
  const subtitle = [tpl.person, tpl.work, tpl.author].filter(Boolean).join(' ')
  const bySub = subtitle ? matchScore({ title: subtitle }, query) : 0
  if (bySub > 0) return Math.min(bySub, 65)
  return matchScore({ title: '', description: tpl.description }, query)
}

export function searchTemplates(all: SearchTemplate[], query: string, limit = 8): SearchTemplate[] {
  return all
    .map((tpl) => ({ tpl, score: templateScore(tpl, query) }))
    .filter((r) => r.score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, limit)
    .map((r) => r.tpl)
}

// Live Convex data wins; otherwise the last cached copy. `null` = nothing to search.
export function pickSource<T>(live: T | undefined, snapshot: T | null): T | null {
  return live ?? snapshot
}
